import React from 'react'

import {
  Combobox,
  Group,
  Input,
  InputBase,
  Text,
  useCombobox,
} from '@mantine/core'

import { Avatar } from '#components/Avatar'
import { useMst } from '#state'
import { IOrg } from '#state/types'
import { getAvatarUrl } from '#util'

import { filterOnLabelDescription, SelectDataItem } from '../util'

import styles from '../Permissions/PermissionUserSelect.module.scss'

export const OwnerSelect = ({
  org,
  onChange,
}: {
  org: IOrg
  onChange: React.Dispatch<
    React.SetStateAction<{
      canSend: boolean
      ownerId: string
    }>
  >
}) => {
  const { environment } = useMst()
  const currentOwnerId = org.owner?.id ?? ''
  const data: SelectDataItem[] = org.alphabetizedMembers.map((user) => {
    return {
      label: user.name,
      value: user.id,
      description: user.email,
      image: getAvatarUrl(user.avatar, environment.config),
      disabled: user.id === currentOwnerId,
    }
  })

  const [search, setSearch] = React.useState('')
  const [selectedId, setSelectedId] = React.useState(currentOwnerId)

  const combobox = useCombobox({
    onDropdownClose: () => {
      combobox.resetSelectedOption()
      setSearch('')
    },
  })

  const selectedItem = data.find((item) => item.value === selectedId)
  const options = filterOnLabelDescription(search, data).map((item) => (
    <UserComboboxOption item={item} key={item.value} />
  ))

  return (
    <Combobox
      classNames={{ dropdown: styles.userselect_dropdown }}
      store={combobox}
      onOptionSubmit={(val) => {
        setSelectedId(val)
        onChange({ canSend: val !== currentOwnerId, ownerId: val })
        combobox.closeDropdown()
      }}
    >
      <Combobox.Target>
        <InputBase
          className={styles.userselect_input}
          component="button"
          type="button"
          label="Owner"
          pointer
          multiline
          rightSection={<Combobox.Chevron />}
          rightSectionPointerEvents="none"
          onClick={() => combobox.toggleDropdown()}
        >
          {selectedItem ? (
            <UserComboboxValue item={selectedItem} />
          ) : (
            <Input.Placeholder>Choose New Owner</Input.Placeholder>
          )}
        </InputBase>
      </Combobox.Target>
      <Combobox.Dropdown>
        <Combobox.Search
          value={search}
          onChange={(event) => setSearch(event.currentTarget.value)}
          placeholder="Search members"
        />
        <Combobox.Options>
          {options.length > 0 ? (
            options
          ) : (
            <Combobox.Empty>Nobody here</Combobox.Empty>
          )}
        </Combobox.Options>
      </Combobox.Dropdown>
    </Combobox>
  )
}

const UserComboboxValue = ({ item }: { item: SelectDataItem }) => (
  <Group wrap="nowrap">
    <Avatar avatarUrl={item.image} />
    <Text>
      {item.label}
      <br />
      <Text span c="dimmed" size="13">
        {item.description}
      </Text>
    </Text>
  </Group>
)

const UserComboboxOption = ({ item }: { item: SelectDataItem }) => (
  <Combobox.Option value={item.value} key={item.value} disabled={item.disabled}>
    <UserComboboxValue item={item} />
  </Combobox.Option>
)
